import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import { useTenant } from "../Context/TenantContext";
import TenantLoginFooter from "./TenantLoginFooter";
import { Mail, Building2, ArrowLeft, CheckCircle2, AlertCircle, Send } from "lucide-react";

// --- Design Tokens ------------------------------------------------------------
const T = {
  border:      "1px solid #E2E8F0",
  radius:      { sm: "8px", md: "12px", lg: "16px", xl: "20px", xxl: "24px", full: "999px" },
  shadow:      { sm: "0 2px 8px rgba(0,0,0,0.07)", lg: "0 8px 40px rgba(79, 70, 229,0.13)" },
  brand:       "var(--primary-blue, #4F46E5)",
  brandLight:  "rgba(79, 70, 229,0.08)",
  brandBorder: "rgba(79, 70, 229,0.18)",
  text: {
    heading:  "#0F172A",
    body:     "#334155",
    muted:    "#64748B",
    disabled: "#94A3B8",
  },
  bg: { main: "#F8FAFC", section: "#FFFFFF" },
};

const inputBase = {
  width: "100%", padding: "11px 14px 11px 42px",
  fontSize: "14px", fontWeight: 600, color: "#0F172A",
  background: "#F8FAFC", border: "1.5px solid #E2E8F0",
  borderRadius: "12px", outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

// --- ForgotPassword -----------------------------------------------------------
const ForgotPassword = () => {
  const navigate = useNavigate();
  const { getCollegeName, getLogoUrl } = useTenant();

  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error,   setError]   = useState("");
  const [focused, setFocused] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");
    setError("");
    try {
      const res = await axios.post(`${API_BASE_URL}/colleges/forgot-password`, { email });
      setSuccess(res.data.message || "Reset link sent. Check your inbox.");
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message;
      setError(msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const collegeName = getCollegeName();
  const logoUrl     = getLogoUrl();

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: T.bg.main, fontFamily: "inherit" }}>
      <main style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "48px 24px",
      }}>
        <div style={{ width: "100%", maxWidth: "420px" }}>

          {/* College badge */}
          <div style={{ textAlign: "center", marginBottom: "28px" }}>
            <div style={{
              width: "60px", height: "60px", borderRadius: T.radius.xl,
              background: T.bg.section, border: `2px solid ${T.brandBorder}`,
              overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px", boxShadow: T.shadow.sm,
            }}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={collegeName}
                  style={{ width: "100%", height: "100%", objectFit: "contain", padding: "6px" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <Building2 style={{ width: "24px", height: "24px", color: T.brand }} />
              )}
            </div>
            {collegeName && (
              <p style={{ fontSize: "14px", fontWeight: 700, color: T.text.heading, margin: 0 }}>
                {collegeName}
              </p>
            )}
          </div>

          {/* Card */}
          <div style={{
            background: T.bg.section, border: T.border,
            borderRadius: T.radius.xxl, boxShadow: T.shadow.lg, overflow: "hidden",
          }}>
            <div style={{ padding: "32px 32px 36px" }}>

              {/* Icon + heading */}
              <div style={{ marginBottom: "28px" }}>
                <div style={{
                  width: "44px", height: "44px", borderRadius: T.radius.lg,
                  background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: "16px",
                }}>
                  <Mail style={{ width: "20px", height: "20px", color: T.brand }} />
                </div>
                <h1 style={{ fontSize: "22px", fontWeight: 800, color: T.text.heading, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
                  Forgot password?
                </h1>
                <p style={{ fontSize: "13px", color: T.text.muted, margin: 0, fontWeight: 500, lineHeight: 1.5 }}>
                  Enter your registered email address and we'll send you a link to reset your password.
                </p>
              </div>

              {/* Success */}
              {success && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: "10px",
                  padding: "12px 14px", borderRadius: T.radius.md,
                  background: "#F0FDF4", border: "1px solid #BBF7D0",
                  marginBottom: "20px",
                }}>
                  <CheckCircle2 style={{ width: "15px", height: "15px", color: "#16A34A", flexShrink: 0, marginTop: "1px" }} />
                  <p style={{ fontSize: "13px", color: "#16A34A", fontWeight: 600, margin: 0 }}>{success}</p>
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: "10px",
                  padding: "12px 14px", borderRadius: T.radius.md,
                  background: "#FEF2F2", border: "1px solid #FECACA",
                  marginBottom: "20px",
                }}>
                  <AlertCircle style={{ width: "15px", height: "15px", color: "#DC2626", flexShrink: 0, marginTop: "1px" }} />
                  <p style={{ fontSize: "13px", color: "#DC2626", fontWeight: 600, margin: 0 }}>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                {/* Email field */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label
                    htmlFor="email"
                    style={{ fontSize: "11px", fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}
                  >
                    Email Address
                  </label>
                  <div style={{ position: "relative" }}>
                    <Mail style={{
                      position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)",
                      width: "15px", height: "15px",
                      color: focused ? T.brand : T.text.disabled,
                      transition: "color 0.15s", pointerEvents: "none",
                    }} />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      required
                      autoComplete="email"
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setFocused(true)}
                      onBlur={() => setFocused(false)}
                      placeholder="you@college.edu"
                      style={{
                        ...inputBase,
                        borderColor: focused ? T.brand : "#E2E8F0",
                        boxShadow:   focused ? `0 0 0 3px ${T.brandLight}` : "none",
                      }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !!success}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                    width: "100%", padding: "13px 20px",
                    background: (loading || success) ? "#93C5FD" : T.brand,
                    color: "#FFFFFF", border: "none", borderRadius: T.radius.md,
                    fontSize: "14px", fontWeight: 800, letterSpacing: "0.04em",
                    textTransform: "uppercase", cursor: (loading || success) ? "not-allowed" : "pointer",
                    boxShadow: (loading || success) ? "none" : "0 4px 16px rgba(79, 70, 229,0.32)",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { if (!loading && !success) e.currentTarget.style.opacity = "0.9"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >
                  <Send style={{ width: "14px", height: "14px" }} />
                  {loading ? "Sending..." : success ? "Link Sent" : "Send Reset Link"}
                </button>
              </form>
            </div>
          </div>

          {/* Back to login */}
          <button
            onClick={() => navigate("/admin/login")}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              width: "100%", marginTop: "16px", padding: "10px",
              background: "none", border: "none", cursor: "pointer",
              fontSize: "13px", fontWeight: 700, color: T.text.muted,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.brand; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.text.muted; }}
          >
            <ArrowLeft style={{ width: "14px", height: "14px" }} />
            Back to login
          </button>

        </div>
      </main>

      <TenantLoginFooter />
    </div>
  );
};

export default ForgotPassword;


